/**
 * The meter curve from docs/SPEC.md section 3. A meter falls along four phases after a care action, holds at 1%
 * for about 6 hours (the last chance), and then the pet dies. Times are "effective hours": real hours after the
 * offline slowdown has been applied (see effective-time.ts).
 */

export interface CurveProfile {
  /** End of the fast first drop, 100% to 88%. */
  phase1End: number;
  /** End of the gradual middle, 88% to 40%. */
  phase2End: number;
  /** End of the second fast drop, 40% to 20%. */
  phase3End: number;
  /** When the meter reaches 1% (end of the slow final stretch, 20% to 1%). Never earlier than 20. */
  onePercentAt: number;
}

export type Curve = Readonly<CurveProfile>;

/** The 1% point is never earlier than this, for any meter of any animal. */
export const MIN_ONE_PERCENT_AT = 20;
/** The last chance: the meter holds at 1% for this long before the pet dies. */
export const LAST_CHANCE_HOURS = 6;

export const DEFAULT_CURVE: Curve = Object.freeze({
  phase1End: 1.5,
  phase2End: 9,
  phase3End: 12,
  onePercentAt: MIN_ONE_PERCENT_AT,
});

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

/** Builds a curve from a partial profile, keeping the rules: ordered phases and a 1% point of at least 20 hours. */
export function makeCurve(profile: Partial<CurveProfile>): Curve {
  const one = Math.max(MIN_ONE_PERCENT_AT, profile.onePercentAt ?? DEFAULT_CURVE.onePercentAt);
  const phase1End = clamp(profile.phase1End ?? DEFAULT_CURVE.phase1End, 0.1, one - 0.3);
  const phase2End = clamp(profile.phase2End ?? DEFAULT_CURVE.phase2End, phase1End + 0.1, one - 0.2);
  const phase3End = clamp(profile.phase3End ?? DEFAULT_CURVE.phase3End, phase2End + 0.1, one);
  return Object.freeze({ phase1End, phase2End, phase3End, onePercentAt: one });
}

/** Effective hours since the last care at which the pet dies. */
export function deathHours(curve: Curve): number {
  return curve.onePercentAt + LAST_CHANCE_HOURS;
}

/** The effective hours without care at which the curve is at `level` (the inverse of `meterValue`, between 1% and 100%). */
export function hoursAtValue(level: number, curve: Curve): number {
  if (level >= 100) return 0;
  if (level <= 1) return curve.onePercentAt;
  const points: ReadonlyArray<readonly [number, number]> = [
    [0, 100],
    [curve.phase1End, 88],
    [curve.phase2End, 40],
    [curve.phase3End, 20],
    [curve.onePercentAt, 1],
  ];
  for (let i = 1; i < points.length; i++) {
    const [t1, v1] = points[i] as readonly [number, number];
    if (level >= v1) {
      const [t0, v0] = points[i - 1] as readonly [number, number];
      return t0 + ((v0 - level) * (t1 - t0)) / (v0 - v1);
    }
  }
  return curve.onePercentAt;
}

/** The meter level (0 to 100) after the given effective hours without care. 0 means the pet has died. */
export function meterValue(effectiveHours: number, curve: Curve): number {
  if (effectiveHours <= 0) return 100;
  if (effectiveHours >= deathHours(curve)) return 0;
  if (effectiveHours >= curve.onePercentAt) return 1;
  const points: ReadonlyArray<readonly [number, number]> = [
    [0, 100],
    [curve.phase1End, 88],
    [curve.phase2End, 40],
    [curve.phase3End, 20],
    [curve.onePercentAt, 1],
  ];
  for (let i = 1; i < points.length; i++) {
    const [t1, v1] = points[i] as readonly [number, number];
    if (effectiveHours <= t1) {
      const [t0, v0] = points[i - 1] as readonly [number, number];
      return v0 + ((v1 - v0) * (effectiveHours - t0)) / (t1 - t0);
    }
  }
  return 1;
}
